/**
 * DatasetList - Dataset list panel component
 * 
 * Displays app datasets (built-in) and user datasets with search/filter
 * 
 * CHANGE HISTORY:
 * - 251201_0150 danielbeer: Created dataset list panel component
 */

import React, { useState } from 'react';
import type { Dataset } from '../types/DatasetTypes';

export interface DatasetListProps {
  appDatasets: Dataset[];
  userDatasets: Dataset[];
  selectedDataset: Dataset | null;
  isLoading: boolean;
  onSelect: (dataset: Dataset) => void;
  onCreate: () => void;
  onImport: (datasetJson: string) => void;
}

export function DatasetList({
  appDatasets,
  userDatasets,
  selectedDataset,
  isLoading,
  onSelect,
  onCreate,
  onImport
}: DatasetListProps) {
  const [filterText, setFilterText] = useState('');

  /**
   * Handle import button click
   */
  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        const text = await file.text();
        onImport(text);
      }
    };
    input.click();
  };

  /**
   * Get status icon for dataset
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '✓';
      case 'syncing': return '⟳';
      case 'error': return '⚠️';
      case 'draft': return '📝';
      default: return '○';
    }
  };

  /**
   * Filter datasets by search text
   */
  const filterDatasets = (datasets: Dataset[]) => {
    if (!filterText) return datasets;
    
    const lowerFilter = filterText.toLowerCase();
    return datasets.filter(d =>
      d.name.toLowerCase().includes(lowerFilter) ||
      d.description?.toLowerCase().includes(lowerFilter) ||
      d.tags?.some(tag => tag.toLowerCase().includes(lowerFilter))
    );
  };

  const filteredAppDatasets = filterDatasets(appDatasets);
  const filteredUserDatasets = filterDatasets(userDatasets);

  return (
    <div className="dataset-list-panel">
      {/* Header */}
      <div className="panel-header">
        <h3>Datasets</h3>
        <div className="panel-actions">
          <button 
            className="import-button" 
            onClick={handleImportClick}
            title="Import Dataset"
          >
            📥
          </button>
          <button 
            className="create-button" 
            onClick={onCreate}
            title="Create New Dataset"
          >
            +
          </button>
        </div>
      </div>

      {/* Search/Filter */}
      <div className="panel-search">
        <input
          type="text"
          placeholder="Filter datasets..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="filter-input"
        />
        {filterText && (
          <button
            onClick={() => setFilterText('')}
            className="clear-filter"
            title="Clear filter"
          >
            ✕
          </button>
        )}
      </div>

      {/* Dataset Lists */}
      <div className="dataset-lists">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner">⟳</div>
            <p>Loading datasets...</p>
          </div>
        ) : (
          <>
            {/* App Datasets */}
            {filteredAppDatasets.length > 0 && (
              <div className="dataset-section">
                <div className="section-header">
                  <h4>App Datasets</h4>
                  <span className="section-count">{filteredAppDatasets.length}</span>
                </div>
                <div className="dataset-items">
                  {filteredAppDatasets.map(dataset => (
                    <div
                      key={dataset.id}
                      className={`dataset-item ${selectedDataset?.id === dataset.id ? 'selected' : ''}`}
                      onClick={() => onSelect(dataset)}
                    >
                      <div className="dataset-item-header">
                        <span className="dataset-status">{getStatusIcon(dataset.status)}</span>
                        <span className="dataset-name">{dataset.name}</span>
                        <span className="dataset-badge app-badge">App</span>
                      </div>
                      {dataset.description && (
                        <p className="dataset-description">{dataset.description}</p>
                      )}
                      <div className="dataset-meta">
                        <span>{dataset.sources?.length || 0} sources</span>
                        {dataset.fields && <span>•</span>}
                        {dataset.fields && <span>{dataset.fields.length} fields</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User Datasets */}
            <div className="dataset-section">
              <div className="section-header">
                <h4>My Datasets</h4>
                <span className="section-count">{filteredUserDatasets.length}</span>
              </div>
              {filteredUserDatasets.length === 0 ? (
                <div className="empty-state">
                  <p>No user datasets yet</p>
                  <button onClick={onCreate} className="create-first-button">
                    Create your first dataset
                  </button>
                </div>
              ) : (
                <div className="dataset-items">
                  {filteredUserDatasets.map(dataset => (
                    <div
                      key={dataset.id}
                      className={`dataset-item ${selectedDataset?.id === dataset.id ? 'selected' : ''}`}
                      onClick={() => onSelect(dataset)}
                    >
                      <div className="dataset-item-header">
                        <span className="dataset-status">{getStatusIcon(dataset.status)}</span>
                        <span className="dataset-name">{dataset.name}</span>
                      </div>
                      {dataset.description && (
                        <p className="dataset-description">{dataset.description}</p>
                      )}
                      <div className="dataset-meta">
                        <span>{dataset.sources?.length || 0} sources</span>
                        {dataset.fields && <span>•</span>}
                        {dataset.fields && <span>{dataset.fields.length} fields</span>}
                      </div>
                      {dataset.tags && dataset.tags.length > 0 && (
                        <div className="dataset-tags">
                          {dataset.tags.map(tag => (
                            <span key={tag} className="tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* No Results */}
            {filterText && filteredAppDatasets.length === 0 && filteredUserDatasets.length === 0 && (
              <div className="no-results">
                <p>No datasets match "{filterText}"</p>
                <button onClick={() => setFilterText('')} className="clear-filter-button">
                  Clear filter
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
